import React, { useState, useMemo } from "react";
import { Tab, Tabs, Button as BsButton } from "react-bootstrap";
import { 
  Wrapper, 
  PublishButton, 
  SaveButton, 
  BackButton, 
  MobileContainerPadding,
 } from "./index.style";
import { showConfirmationPopUp } from "components/popup/confirmationModal";
import { TrackInfoFormCard } from "./components/trackInfoFormCard/TrackInfoFormCard";
import { TrackLessonsFormCard } from "./components/trackLessonsFormCard/TrackLessonsFormCard";
import { UploadProgress } from "./components/UploadProgress";

import { useTrackInfo } from "./hooks/useTrackInfo";
import { useLessons } from "./hooks/useLessons";
import { useTrackCreation } from "./hooks/useTrackCreation";
import { showToast } from "components/toast";
import { validateLesson } from "../create/components/trackLessonsFormCard/TrackLessonsFormCard";

export const CreateTrackForm: React.FC = () => {
  const [activeKey, setActiveKey] = useState<"info" | "lessons">("info");
  const [currentAction, setCurrentAction] = useState<'save' | 'publish' | null>(null);

  const {
    draft,
    setDraft,
    errors,
    fileRef,
    isValid,
    validate,
    handleFile,
    triggerUpload,
    removeImage,
  } = useTrackInfo();

  const {
    lessons,
    patchLesson,
    moveDown,
    moveUp,
    duplicate,
    removeLesson,
    addLesson,
  } = useLessons();

  const {
    saveTrack,
    publishTrack,
    handleCompleteFlow,
    isLoading,
    uploadProgress,
    uploadError,
    uploadSteps,
    currentStep,
    isUploading
  } = useTrackCreation();

  const isSaveValid = useMemo(() => {
    return draft.title.trim().length > 0 &&
      draft.description.trim().length > 0 &&
      !!(draft.imageFile || draft.imageUrl);
  }, [draft]);

  const isPublishValid = useMemo(() => {
    const hasValidInfo = draft.title.trim().length > 0 &&
      draft.description.trim().length > 0 &&
      !!(draft.imageFile || draft.imageUrl);

    const hasValidFirstLesson = lessons.length > 0 &&
      lessons[0].title.trim().length > 0 &&
      lessons[0].description.trim().length > 0;

    return hasValidInfo && hasValidFirstLesson;
  }, [draft, lessons]);

  const save = async () => {
    if (!isSaveValid) {
      setActiveKey("info");
      return;
    }

    const hasValidLessons =
      lessons.length > 0 &&
      lessons.every(
        (lesson) => validateLesson(lesson)
      );

    if (!hasValidLessons) {
      setActiveKey("lessons");
      showToast(
        "error",
        "Por favor, preencha todas as informações das aulas antes de salvar."
      );
      return;
    }



    try {
      setCurrentAction('save');
      const videoFiles: { [lessonIndex: number]: File } = {};
      const attachmentFiles: { [lessonIndex: number]: File[] } = {};

      lessons.forEach((lesson, index) => {

        if (lesson.videoFile) {
          videoFiles[index] = lesson.videoFile;
        }
        if (lesson.attachments && lesson.attachments.length > 0) {
          attachmentFiles[index] = lesson.attachments;
        }
      });

      await handleCompleteFlow(draft, lessons, 'Draft', videoFiles, attachmentFiles);
    } catch (error) {
      console.error('Erro ao salvar trilha:', error);
    } finally {
      setCurrentAction(null);
    }
  };

  const publish = async () => {
    if (!isPublishValid) {
      setActiveKey("info");
      return;
    }

    try {
      setCurrentAction('publish');
      const videoFiles: { [lessonIndex: number]: File } = {};
      const attachmentFiles: { [lessonIndex: number]: File[] } = {};

      lessons.forEach((lesson, index) => {
        if (lesson.videoFile) {
          videoFiles[index] = lesson.videoFile;
        }
        if (lesson.attachments && lesson.attachments.length > 0) {
          attachmentFiles[index] = lesson.attachments;
        }
      });

      await handleCompleteFlow(draft, lessons, 'Publish', videoFiles, attachmentFiles);
    } catch (error) {
      console.error('Erro ao publicar trilha:', error);
    } finally {
      setCurrentAction(null);
    }
  };

  const confirmPublish = () => {
    showConfirmationPopUp({
      title: "Publicar trilha",
      message: "Tem certeza de que deseja publicar trilha?",
      onConfirm: () => { publish(); }
    });
  };

  const goNext = () => {
    if (!isSaveValid) {
      setActiveKey("info");
      return;
    }
    setActiveKey("lessons");
  };

  const getValidationMessage = () => {
    const issues: string[] = [];

    if (!draft.title.trim()) issues.push("Título da trilha");
    if (!draft.description.trim()) issues.push("Descrição da trilha");
    if (!(draft.imageFile || draft.imageUrl)) issues.push("Imagem da trilha");

    return issues.length > 0 ? `Preencha: ${issues.join(", ")}` : null;
  };

  const getPublishValidationMessage = () => {
    const issues: string[] = [];

    if (!draft.title.trim()) issues.push("Título da trilha");
    if (!draft.description.trim()) issues.push("Descrição da trilha");
    if (!(draft.imageFile || draft.imageUrl)) issues.push("Imagem da trilha");
    if (lessons.length === 0) issues.push("Pelo menos uma aula");
    if (lessons.length > 0 && (!lessons[0].title.trim() || !lessons[0].description.trim())) {
      issues.push("Título e descrição da primeira aula");
    }

    return issues.length > 0 ? `Para publicar, preencha: ${issues.join(", ")}` : null;
  };

  return (
    <>
      <MobileContainerPadding />
      <Wrapper>
      <div 
        className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2"
      >
        <div style={{ fontFamily: 'sans-serif', fontWeight: 700, fontSize: 28, lineHeight: '120%', letterSpacing: 0, color: '#464646' }}>
          Criar trilha
        </div>
        <div className="d-flex gap-2">
          <SaveButton
            onClick={save}
            disabled={!isSaveValid || isLoading || isUploading}
            style={{
              opacity: (!isSaveValid || isLoading || isUploading) ? 0.5 : 1,
              cursor: (!isSaveValid || isLoading || isUploading) ? 'not-allowed' : 'pointer'
            }}
            title={!isSaveValid ? getValidationMessage() : undefined}
          >
            {currentAction === 'save' ? "Processando..." : "Salvar"}
          </SaveButton>
          <PublishButton
            onClick={confirmPublish}
            disabled={!isPublishValid || isLoading || isUploading}
            style={{
              opacity: (!isPublishValid || isLoading || isUploading) ? 0.5 : 1,
              cursor: (!isPublishValid || isLoading || isUploading) ? 'not-allowed' : 'pointer'
            }}
            title={!isPublishValid ? getPublishValidationMessage() : undefined}
          >
            {currentAction === 'publish' ? "Processando..." : "Publicar"}
          </PublishButton>
        </div>
      </div>

      <UploadProgress
        isVisible={isLoading}
        progress={uploadProgress}
        error={uploadError}
        steps={uploadSteps}
        currentStep={currentStep || undefined}
        message="Criando trilha e fazendo upload dos arquivos..."
      />

      <Tabs
        id="create-track-tabs"
        activeKey={activeKey}
        onSelect={(k) => k && setActiveKey(k as any)}
        style={{ marginTop: 0 }}
      >
        <Tab eventKey="info" title="Informações gerais">
          <TrackInfoFormCard
            draft={draft}
            errors={errors}
            fileRef={fileRef}
            onChange={(field, value) => setDraft({ ...draft, [field]: value })}
            handleFile={handleFile}
            triggerUpload={triggerUpload}
            removeImage={removeImage}
          />
          <div className="d-flex justify-content-end mt-4">
            <SaveButton onClick={goNext}>
              Próximo
            </SaveButton>
          </div>
        </Tab>

        <Tab eventKey="lessons" title="Adicionar aulas">
          {lessons.map((lesson, i) => (
            <div key={lesson.id} className="mb-3">
              <TrackLessonsFormCard
                index={i}
                lesson={lesson}
                onChange={(patch) => patchLesson(i, patch)}
                onMoveUp={() => moveUp(i)}
                onMoveDown={() => moveDown(i)}
                onDuplicate={() => duplicate(i)}
                onDelete={() => removeLesson(i)}
                onAddLesson={i === lessons.length - 1 ? addLesson : undefined}
              />
            </div>
          ))}

          <div className="d-flex justify-content-between mt-3">
            <BackButton onClick={() => setActiveKey("info")}>Voltar</BackButton>
          </div>
        </Tab>
      </Tabs>
    </Wrapper>
    </>
  );
};
